import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './Auth.css';

const ForgotPassword = ({ onSwitchToLogin }) => {
    const { forgotPassword, confirmForgotPassword, loading, error, clearError } = useAuth();
    const [step, setStep] = useState(1); // 1: email input, 2: confirmation code input
    const [formData, setFormData] = useState({
        email: '',
        confirmationCode: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [formErrors, setFormErrors] = useState({});
    const [isResetComplete, setIsResetComplete] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear field error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const validateEmailForm = () => {
        const errors = {};
        
        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const validateResetForm = () => {
        const errors = {};
        
        if (!formData.confirmationCode.trim()) {
            errors.confirmationCode = 'Confirmation code is required';
        }
        
        if (!formData.newPassword) {
            errors.newPassword = 'New password is required';
        } else if (formData.newPassword.length < 8) {
            errors.newPassword = 'Password must be at least 8 characters';
        } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])/.test(formData.newPassword)) {
            errors.newPassword = 'Password must contain uppercase, lowercase, number and special character';
        }
        
        if (!formData.confirmPassword) {
            errors.confirmPassword = 'Please confirm your password';
        } else if (formData.newPassword !== formData.confirmPassword) {
            errors.confirmPassword = 'Passwords do not match';
        }
        
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleEmailSubmit = async (e) => {
        e.preventDefault();
        clearError();
        
        if (!validateEmailForm()) {
            return;
        }

        try {
            await forgotPassword(formData.email);
            setStep(2);
        } catch (err) {
            console.error('Forgot password error:', err);
        }
    };

    const handleResetSubmit = async (e) => {
        e.preventDefault();
        clearError();
        
        if (!validateResetForm()) {
            return;
        }

        try {
            await confirmForgotPassword(formData.email, formData.confirmationCode, formData.newPassword);
            setIsResetComplete(true);
        } catch (err) {
            console.error('Reset password error:', err);
        }
    };

    if (isResetComplete) {
        return (
            <div className="auth-container">
                <div className="auth-form">
                    <h2>Password Reset Successful!</h2>
                    <p className="auth-subtitle">Your password has been successfully reset. You can now sign in with your new password.</p>
                    
                    <button 
                        type="button" 
                        className="auth-button"
                        onClick={onSwitchToLogin}
                    >
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    if (step === 2) {
        return (
            <div className="auth-container">
                <div className="auth-form">
                    <h2>Reset Your Password</h2>
                    <p className="auth-subtitle">
                        Enter the confirmation code sent to <strong>{formData.email}</strong> and your new password.
                    </p>
                    
                    {error && <div className="error-message">{error}</div>}
                    
                    <form onSubmit={handleResetSubmit}>
                        <div className="form-group">
                            <label htmlFor="confirmationCode">Confirmation Code</label>
                            <input
                                type="text"
                                id="confirmationCode"
                                name="confirmationCode"
                                value={formData.confirmationCode}
                                onChange={handleInputChange}
                                className={formErrors.confirmationCode ? 'error' : ''}
                                placeholder="Enter confirmation code"
                                maxLength="6"
                            />
                            {formErrors.confirmationCode && <span className="field-error">{formErrors.confirmationCode}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="newPassword">New Password</label>
                            <input
                                type="password"
                                id="newPassword"
                                name="newPassword"
                                value={formData.newPassword}
                                onChange={handleInputChange}
                                className={formErrors.newPassword ? 'error' : ''}
                                placeholder="Enter new password"
                            />
                            {formErrors.newPassword && <span className="field-error">{formErrors.newPassword}</span>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Confirm New Password</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                className={formErrors.confirmPassword ? 'error' : ''}
                                placeholder="Confirm new password"
                            />
                            {formErrors.confirmPassword && <span className="field-error">{formErrors.confirmPassword}</span>}
                        </div>

                        <button 
                            type="submit" 
                            className="auth-button"
                            disabled={loading}
                        >
                            {loading ? 'Resetting Password...' : 'Reset Password'}
                        </button>
                    </form>

                    <div className="auth-links">
                        <button 
                            type="button" 
                            className="link-button"
                            onClick={onSwitchToLogin}
                        >
                            Back to Sign In
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="auth-container">
            <div className="auth-form">
                <h2>Forgot Password</h2>
                <p className="auth-subtitle">Enter your email address and we'll send you a confirmation code to reset your password.</p>
                
                {error && <div className="error-message">{error}</div>}
                
                <form onSubmit={handleEmailSubmit}>
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className={formErrors.email ? 'error' : ''}
                            placeholder="Enter your email"
                        />
                        {formErrors.email && <span className="field-error">{formErrors.email}</span>}
                    </div>

                    <button 
                        type="submit" 
                        className="auth-button"
                        disabled={loading}
                    >
                        {loading ? 'Sending Code...' : 'Send Reset Code'}
                    </button>
                </form>

                <div className="auth-links">
                    <span>Remember your password? </span>
                    <button 
                        type="button" 
                        className="link-button"
                        onClick={onSwitchToLogin}
                    >
                        Sign In
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword; 